import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';

// Layout
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';

// Guards
import ProtectedRoute from './components/auth/ProtectedRoute';

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center gap-4">
      <div className="spinner w-10 h-10"></div>
      <p className="text-gray-500 text-sm">Cargando...</p>
    </div>
  </div>
);

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TenantsPage = lazy(() => import('./pages/TenantsPage'));
const TenantOnboardingPage = lazy(() => import('./pages/TenantOnboardingPage'));
const TenantDetailPage = lazy(() => import('./pages/TenantDetailPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const AlertsPage = lazy(() => import('./pages/AlertsPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const GeoLeadsMapPage = lazy(() => import('./pages/GeoLeadsMapPage'));
const PlansPage = lazy(() => import('./pages/PlansPage'));
const PlanEditorPage = lazy(() => import('./pages/PlanEditorPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            {/* Dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Tenant Management */}
            <Route path="/tenants" element={<TenantsPage />} />
            <Route path="/tenants/new" element={<TenantOnboardingPage />} />
            <Route path="/tenants/onboarding/:draftId" element={<TenantOnboardingPage />} />
            <Route path="/tenants/:id" element={<TenantDetailPage />} />

            {/* Analytics & Monitoring */}
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/audit-log" element={<AuditLogPage />} />

            {/* Billing & Administration */}
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/geoleads" element={<GeoLeadsMapPage />} />

            {/* Subscription Plans */}
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/plans/new" element={<PlanEditorPage />} />
            <Route path="/plans/:id/edit" element={<PlanEditorPage />} />

            {/* Settings */}
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
